import { DragEscapeGuard } from 'src/modules/shared/components/atoms/drag-escape-guard';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import React, { useDeferredValue, useState } from 'react';
import {
  PiArrowsClockwise,
  PiArrowsOutCardinal,
  PiChartBar,
  PiPlus,
} from 'react-icons/pi';
import {
  ReportWidgetSize,
  type ReportDashboardWidgetInstance,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { useAlert } from 'src/contexts/alert-context';
import { useFunnels, useFunnelStatuses } from 'src/hooks/api/funnels';
import { DraggableItem } from 'src/modules/shared/components/molecules/draggable-item';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useFunnelCustomFieldDefinitions } from 'src/modules/shared/hooks/api/crm/funnels/funnel-custom-field-definitions';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { ReportPermission } from 'src/modules/shared/types/permissions.type';
import { classes } from 'src/modules/shared/utils/styles.util';
import {
  useReportWidgetCatalog,
  useReportWidgetUsers,
  useReportsDashboard,
  useResetReportsDashboard,
  useUpdateReportsDashboard,
} from '../hooks/reports-dashboard';
import {
  createReportWidgetDraft,
  duplicateReportWidget,
  normalizeDashboardWidgetPositions,
  removeDashboardWidget,
  reorderDashboardWidgets,
  toReportWidgetKind,
  upsertDashboardWidget,
} from '../utils/report-dashboard';
import {
  cloneWidget,
  type DashboardWidgetKind,
  getWidgetFunnelId,
  supportsWidgetUserFilter,
  toDashboardWidgetKind,
} from './reports-dashboard.utils';
import { ReportDashboardWidgetCard } from './report-dashboard-widget-card';
import { WidgetCatalogPanel } from './widget-catalog-panel';
import { WidgetEditorPanel } from './widget-editor-panel';
import { ReportsDashboardSkeleton } from './reports-dashboard-skeleton';
import { getReportDateRangeValidationMessage } from '../utils/report-date-range';

const BREAKDOWN_FIELD_TYPES = new Set(['STRING', 'BOOLEAN', 'LIST']);
const DND_DROP_ANIMATION = {
  duration: 200,
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.45',
      },
    },
  }),
};

const ReportsDashboardHeaderActions: React.FC<{
  canUpdateDashboard: boolean;
  canDeleteDashboard: boolean;
  resetLoading: boolean;
  onAddWidget: () => void;
  onResetClick: () => void;
}> = ({
  canUpdateDashboard,
  canDeleteDashboard,
  resetLoading,
  onAddWidget,
  onResetClick,
}) => {
  const { t } = useI18n();

  if (!canUpdateDashboard && !canDeleteDashboard) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canUpdateDashboard ? (
        <Button
          size="md"
          variant="primary"
          onClick={onAddWidget}
          className="flex items-center gap-2"
        >
          <PiPlus className="h-4 w-4" />
          {t('reports.dashboard.actions.addWidget')}
        </Button>
      ) : null}
      {canDeleteDashboard ? (
        <Button
          size="md"
          appearance="ghost"
          variant="secondary"
          onClick={onResetClick}
          loading={resetLoading}
          className="flex items-center gap-2"
        >
          <PiArrowsClockwise className="h-4 w-4" />
          {t('reports.dashboard.actions.reset')}
        </Button>
      ) : null}
    </div>
  );
};

const ReportsDashboardDragHandle: React.FC<{
  DragHandle: React.ComponentType<React.HTMLAttributes<HTMLDivElement>>;
  label: string;
}> = ({ DragHandle, label }) => (
  <DragHandle className="inline-flex cursor-grab items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-neutral-600 hover:border-neutral-300 hover:text-neutral-800 active:cursor-grabbing">
    <PiArrowsOutCardinal className="h-4 w-4" />
    <Text
      as="span"
      size="tiny"
      weight="medium"
      className="uppercase tracking-wide"
    >
      {label}
    </Text>
  </DragHandle>
);

/**
 * Dashboard tab for report widgets.
 */
export const ReportsDashboard: React.FC = () => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const dashboardQuery = useReportsDashboard();
  const catalogQuery = useReportWidgetCatalog();
  const updateDashboard = useUpdateReportsDashboard();
  const resetDashboard = useResetReportsDashboard();
  const { funnels, loading: funnelsLoading } = useFunnels();
  const {
    hasPermission: canUpdateDashboard,
    loading: updatePermissionLoading,
  } = useHasPermissions(ReportPermission.DashboardUpdate);
  const {
    hasPermission: canDeleteDashboard,
    loading: deletePermissionLoading,
  } = useHasPermissions(ReportPermission.DashboardDelete);

  const [showCatalog, setShowCatalog] = useState(false);
  const [editorDraft, setEditorDraft] =
    useState<ReportDashboardWidgetInstance | null>(null);
  const [activeWidgetId, setActiveWidgetId] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const deferredDraft = useDeferredValue(editorDraft);

  const selectedFunnelId = getWidgetFunnelId(editorDraft);
  const shouldLoadWidgetUsers = Boolean(
    editorDraft && supportsWidgetUserFilter(editorDraft.kind)
  );
  const funnelStatusesQuery = useFunnelStatuses(selectedFunnelId);
  const widgetUsersQuery = useReportWidgetUsers(shouldLoadWidgetUsers);
  const breakdownDefinitionsQuery = useFunnelCustomFieldDefinitions({
    funnelId: selectedFunnelId,
    enabled: Boolean(selectedFunnelId),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const dashboardView = dashboardQuery.dashboard;
  const currentConfig = dashboardView?.config;
  const activeWidget =
    currentConfig?.widgets.find((widget) => widget.id === activeWidgetId) ??
    null;
  const widgetTitleByKind = new Map(
    (dashboardView?.availableWidgets ?? []).map((widget) => [
      toDashboardWidgetKind(widget.kind),
      widget.title,
    ])
  );

  const availableBreakdownDefinitions =
    breakdownDefinitionsQuery.definitions.filter((definition) =>
      BREAKDOWN_FIELD_TYPES.has(definition.fieldType)
    );
  const isSetupModalOpen =
    canUpdateDashboard && (showCatalog || Boolean(editorDraft));

  const handleOpenCatalog = () => {
    setShowCatalog(true);
    setEditorDraft(null);
  };

  const handleStartAddWidget = (kind: DashboardWidgetKind) => {
    if (!dashboardView) {
      showAlert(t('reports.dashboard.errors.defaultWidgetNotFound'), 'error');
      return;
    }

    const nextPosition = currentConfig?.widgets.length ?? 0;
    const templateWidget = dashboardView.defaultConfig.widgets.find(
      (widget) => toReportWidgetKind(widget.kind) === kind
    );

    if (!templateWidget) {
      showAlert(t('reports.dashboard.errors.defaultWidgetNotFound'), 'error');
      return;
    }

    setEditorDraft(
      createReportWidgetDraft({
        templateWidget,
        position: nextPosition,
      })
    );
    setShowCatalog(false);
  };

  const handleEditWidget = (widget: ReportDashboardWidgetInstance) => {
    setEditorDraft(cloneWidget(widget));
    setShowCatalog(false);
  };

  const handleCancelEditor = () => {
    setEditorDraft(null);
  };

  const handleCloseSetupModal = () => {
    setShowCatalog(false);
    setEditorDraft(null);
  };

  const handleSaveDraft = async () => {
    if (!canUpdateDashboard) {
      return;
    }

    if (!currentConfig || !editorDraft) {
      return;
    }

    const dateRangeError = getReportDateRangeValidationMessage(
      editorDraft.filters.dateRange,
      t
    );
    if (dateRangeError) {
      showAlert(dateRangeError, 'error');
      return;
    }

    try {
      await updateDashboard.mutateAsync(
        upsertDashboardWidget(currentConfig, {
          ...editorDraft,
          layout: {
            ...editorDraft.layout,
            position: editorDraft.layout.position,
          },
        })
      );
      setEditorDraft(null);
      showAlert(t('reports.dashboard.alerts.updated'), 'success');
    } catch {
      showAlert(t('reports.dashboard.errors.saveWidget'), 'error');
    }
  };

  const handleDuplicateWidget = async (
    widget: ReportDashboardWidgetInstance
  ) => {
    if (!canUpdateDashboard) {
      return;
    }

    if (!currentConfig) {
      return;
    }

    try {
      await updateDashboard.mutateAsync(
        upsertDashboardWidget(
          {
            ...currentConfig,
            widgets: normalizeDashboardWidgetPositions(currentConfig.widgets),
          },
          duplicateReportWidget(
            widget,
            currentConfig.widgets.length,
            t('reports.dashboard.duplicateSuffix')
          )
        )
      );
      showAlert(t('reports.dashboard.alerts.duplicated'), 'success');
    } catch {
      showAlert(t('reports.dashboard.errors.duplicateWidget'), 'error');
    }
  };

  const handleRemoveWidget = async (widgetId: string) => {
    if (!canUpdateDashboard) {
      return;
    }

    if (!currentConfig) {
      return;
    }

    try {
      await updateDashboard.mutateAsync(
        removeDashboardWidget(currentConfig, widgetId)
      );
      if (editorDraft?.id === widgetId) {
        setEditorDraft(null);
      }
      showAlert(t('reports.dashboard.alerts.removed'), 'success');
    } catch {
      showAlert(t('reports.dashboard.errors.removeWidget'), 'error');
    }
  };

  const handleResetDashboard = async () => {
    if (!canDeleteDashboard) {
      return;
    }

    try {
      await resetDashboard.mutateAsync();
      setEditorDraft(null);
      setShowCatalog(false);
      setShowResetConfirm(false);
      showAlert(t('reports.dashboard.alerts.reset'), 'success');
    } catch {
      showAlert(t('reports.dashboard.errors.resetDashboard'), 'error');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveWidgetId(null);

    if (!canUpdateDashboard) {
      return;
    }

    if (!currentConfig || !event.over || event.active.id === event.over.id) {
      return;
    }

    const orderedIds = currentConfig.widgets.map((widget) => widget.id);
    const oldIndex = orderedIds.indexOf(String(event.active.id));
    const newIndex = orderedIds.indexOf(String(event.over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const nextIds = arrayMove(orderedIds, oldIndex, newIndex);
    try {
      await updateDashboard.mutateAsync(
        reorderDashboardWidgets(currentConfig, nextIds)
      );
    } catch {
      showAlert(t('reports.dashboard.errors.reorderDashboard'), 'error');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveWidgetId(String(event.active.id));
  };

  const handleDragCancel = () => {
    setActiveWidgetId(null);
  };

  if (
    dashboardQuery.loading ||
    catalogQuery.loading ||
    updatePermissionLoading ||
    deletePermissionLoading
  ) {
    return <ReportsDashboardSkeleton />;
  }

  if (
    dashboardQuery.error ||
    catalogQuery.error ||
    !dashboardView ||
    !currentConfig
  ) {
    return (
      <ErrorState
        title={t('reports.dashboard.errors.loadDashboardTitle')}
        description={t('reports.dashboard.errors.loadDashboardDescription')}
        onAction={() => {
          void dashboardQuery.refetch();
          void catalogQuery.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PanelHeader
        className="border-b border-neutral-200 bg-white px-4 py-3"
        actions={
          <ReportsDashboardHeaderActions
            canUpdateDashboard={canUpdateDashboard}
            canDeleteDashboard={canDeleteDashboard}
            resetLoading={resetDashboard.loading}
            onAddWidget={handleOpenCatalog}
            onResetClick={() => setShowResetConfirm(true)}
          />
        }
      >
        <div className="flex items-center gap-4">
          <PiChartBar className="h-7 w-7 flex-shrink-0" />
          <div className="space-y-1">
            <Text as="h2" size="h2" weight="medium">
              {t('reports.dashboard.pageTitle')}
            </Text>
            <Text size="small" color="secondary" className="hidden md:block">
              {t('reports.dashboard.pageDescription')}
            </Text>
          </div>
        </div>
      </PanelHeader>

      <Modal
        isOpen={isSetupModalOpen}
        onClose={handleCloseSetupModal}
        dialogClassName={classes(
          'h-[100dvh] max-h-[100dvh] w-full rounded-none sm:h-[96dvh] sm:max-h-[96dvh] sm:rounded-2xl',
          editorDraft
            ? 'sm:w-[min(96vw,1260px)] sm:max-w-[1260px] xl:w-[min(96vw,1440px)] xl:max-w-[1440px]'
            : 'sm:w-[min(96vw,1120px)] sm:max-w-[1120px]'
        )}
        headerClassName="px-4 py-3 sm:px-5 sm:py-4"
        bodyClassName="min-h-0 flex-col overflow-x-hidden overflow-y-auto px-0 py-0 xl:overflow-hidden"
        header={
          <div className="space-y-1">
            <Text
              as="h3"
              size="h6"
              weight="semibold"
              className="text-primary-900"
            >
              {editorDraft
                ? t('reports.dashboard.modal.configureTitle')
                : t('reports.dashboard.modal.addTitle')}
            </Text>
            <Text size="small" className="text-neutral-600">
              {editorDraft
                ? t('reports.dashboard.modal.configureDescription')
                : t('reports.dashboard.modal.addDescription')}
            </Text>
          </div>
        }
        body={
          showCatalog ? (
            <WidgetCatalogPanel
              items={catalogQuery.catalog}
              onSelect={handleStartAddWidget}
              inModal
            />
          ) : editorDraft ? (
            <div className="flex h-full min-h-0 max-h-full w-full flex-1 flex-col overflow-hidden">
              <WidgetEditorPanel
                key={editorDraft.id}
                draft={editorDraft}
                widgetTitleByKind={widgetTitleByKind}
                onChange={setEditorDraft}
                onCancel={handleCancelEditor}
                onSave={handleSaveDraft}
                saving={updateDashboard.loading}
                funnels={funnels}
                funnelsLoading={funnelsLoading}
                statuses={funnelStatusesQuery.funnelStatuses}
                statusesLoading={funnelStatusesQuery.loading}
                userOptions={widgetUsersQuery.users}
                usersLoading={widgetUsersQuery.loading}
                breakdownDefinitions={availableBreakdownDefinitions}
                previewDraft={deferredDraft}
                inModal
              />
            </div>
          ) : null
        }
        footer={
          editorDraft && !showCatalog && canUpdateDashboard ? (
            <ModalFooter
              onCancel={handleCancelEditor}
              onConfirm={handleSaveDraft}
              cancelText={t('reports.dashboard.actions.cancel')}
              confirmText={t('reports.dashboard.actions.save')}
              confirmDisabled={
                updateDashboard.loading ||
                Boolean(
                  getReportDateRangeValidationMessage(
                    editorDraft.filters.dateRange,
                    t
                  )
                )
              }
              loading={updateDashboard.loading}
            />
          ) : undefined
        }
      />
      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={() => void handleResetDashboard()}
        loading={resetDashboard.loading}
        variant="secondary"
        title={t('reports.dashboard.resetConfirm.title')}
        confirmText={t('reports.dashboard.resetConfirm.confirm')}
        cancelText={t('reports.dashboard.resetConfirm.cancel')}
      >
        <Text size="small" color="secondary">
          {t('reports.dashboard.resetConfirm.description')}
        </Text>
      </ConfirmModal>

      <div className="flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-[1500px] space-y-5 px-4 py-4 md:px-6 lg:px-8">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
          >
            <DragEscapeGuard />
            <SortableContext
              items={currentConfig.widgets.map((widget) => widget.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {currentConfig.widgets.map((widget) => (
                  <DraggableItem
                    key={widget.id}
                    id={widget.id}
                    data={widget}
                    disabled={!canUpdateDashboard}
                    unstyled
                    className={
                      widget.layout.size === ReportWidgetSize.FULL
                        ? 'md:col-span-2'
                        : 'md:col-span-1'
                    }
                  >
                    {({ DragHandle, isDragging }) => (
                      <div
                        className={classes(
                          'transition-opacity duration-200',
                          isDragging && 'opacity-40'
                        )}
                      >
                        <ReportDashboardWidgetCard
                          widget={widget}
                          widgetTitleByKind={widgetTitleByKind}
                          onEdit={() => handleEditWidget(widget)}
                          onDuplicate={() => handleDuplicateWidget(widget)}
                          onRemove={() => handleRemoveWidget(widget.id)}
                          readOnly={!canUpdateDashboard}
                          dragHandle={
                            canUpdateDashboard ? (
                              <ReportsDashboardDragHandle
                                DragHandle={DragHandle}
                                label={t('reports.dashboard.actions.drag')}
                              />
                            ) : undefined
                          }
                        />
                      </div>
                    )}
                  </DraggableItem>
                ))}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={DND_DROP_ANIMATION}>
              {activeWidget ? (
                <div className="pointer-events-none w-[min(92vw,720px)] rotate-1 opacity-90 shadow-2xl">
                  <ReportDashboardWidgetCard
                    widget={activeWidget}
                    widgetTitleByKind={widgetTitleByKind}
                    isPreview
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
    </div>
  );
};
